/*
 * Copyright (c) 2024 Amazon.com, Inc. All Rights Reserved.
 */
import "./CommitHistoryPage.css";
import { CommitHistoryTable } from "./CommitHistoryTable";
import { Region } from "../../model/Region";
import { DetectionSystem } from "../../model/DetectionSystem";
import { CommitEntry } from "../../model/CommitEntry";
import { CommitType } from "../../model/CommitType";
import { Navbar, NavbarPages } from "../navbar/Navbar";
import { useEffect, useState } from "react";
import sentryFetch from "@amzn/sentry-fetch";
import { StageConfig } from "../../config/StageConfig";
import { RequestType } from "../../model/RequestType";
import { DateFilterValues, NumberFilterValues } from "../../model/FilterValues";
import { getTimeFromDate } from "../../config/DateUtils";
import { Snackbar } from "@mui/material";
import { isJSONResponse } from "../../config/Requests";

/**
 * @author stcatai Mar 22, 2024
 */
export function CommitHistoryPage() {
  const PAGE_SIZES = [10, 20, 30, 40, 50];

  const [stageConfig, setStageConfig] = useState<StageConfig>();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [valuesPerPage, setValuesPerPage] = useState<number>(10);
  const [region, setRegion] = useState<Region>();
  const [detectionSystem, setDetectionSystem] = useState<DetectionSystem>();
  const [commitType, setCommitType] = useState<CommitType>();
  const [publishable, setPublishable] = useState<boolean>();
  const [label, setLabel] = useState<string>();
  const [modelName, setModelName] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [author, setAuthor] = useState<string>();
  const [ttl, setTtl] = useState<NumberFilterValues>({});
  const [commitDate, setCommitDate] = useState<DateFilterValues>({});
  const [commits, setCommits] = useState<CommitEntry[]>([]);

  useEffect(() => {
    fetch("../stageConfig.json")
      .then(res => res.json())
      .then(stageConfig => setStageConfig(stageConfig));
  }, []);

  useEffect(() => {
    if (!stageConfig) return;
    sentryFetch(`${stageConfig.url}/modelsManager?requestType=${RequestType.SEARCH_COMMITS}`+
      `&region=${region || ""}&detectionSystem=${detectionSystem || ""}&commitType=${commitType || ""}`+
      `&publishable=${publishable || ""}&label=${label || ""}&modelName=${modelName || ""}`+
      `&ttlGreaterThan=${ttl.greater || ""}&ttlLessThan=${ttl.less || ""}&ttlEquals=${ttl.equal || ""}`+
      `&fromTimestamp=${getTimeFromDate(commitDate.fromDate) || ""}&toTimestamp=${getTimeFromDate(commitDate.toDate) || ""}`+
      `&description=${description || ""}&author=${author || ""}`+
      `&page=${page}&valuesPerPage=${valuesPerPage}`, {
      method: "GET",
      credentials: "include",
      sentryOptions: {
        followMidwayStepUp: true
      }
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          return response.text().then(response => {
            if (isJSONResponse(response)) {
              throw new Error(JSON.parse(response).message);
            } else {
              throw new Error(response);
            }
          });
        }
      })
      .then(commits => setCommits(commits))
      .catch(error => {
        console.error(error.message);
        setResponseMessage(error.message);
        setShowMessage(true);
      });
  }, [stageConfig, page, valuesPerPage, region, detectionSystem, commitType,
    publishable, label, modelName, ttl, commitDate, description, author]);

  return <>
    {stageConfig && <div className="commit-history-page">
      <Navbar selectedPage={NavbarPages.CommitHistory} stage={stageConfig.stage}/>
      <div className="table-container">
        <div className="page-buttons">
          <label htmlFor="perPage">Page size:</label>
          <select id="perPage" defaultValue={10} onChange={(e) => setValuesPerPage(parseInt(e.target.value))}>
            {PAGE_SIZES.map((pageSize, idx) =>
              <option value={pageSize} key={idx}>{pageSize}</option>
            )}
          </select>
          <button onClick={() => setPage(page-1)} disabled={page === 1}>{"<"}</button>
          <span>Page {page}</span>
          <button onClick={() => setPage(page+1)} disabled={commits && commits.length < valuesPerPage}>{">"}</button>
        </div>
        <CommitHistoryTable commits={commits} setRegionFetch={setRegion} setDetectionSystemFetch={setDetectionSystem}
                            setCommitTypeFetch={setCommitType} setPublishableFetch={setPublishable} setLabelFetch={setLabel}
                            setModelNameFetch={setModelName} setDescriptionFetch={setDescription} setAuthorFetch={setAuthor}
                            setTtlFetch={setTtl} setCommitDateFetch={setCommitDate}/>
        <Snackbar
          open={showMessage}
          autoHideDuration={5000}
          onClose={() => setShowMessage(false)}
          message={responseMessage}
          anchorOrigin={{vertical: "bottom", horizontal: "center"}}
        />
      </div>
    </div>}
  </>
}